import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TrendingDown } from "lucide-react";
import {
  OFF_PADSPLIT_SURCHARGE_CENTS,
  VOLUME_DISCOUNT_TIERS,
  volumeDiscountPercent,
} from "@shared/modular-pricing";
import { MAX_ROOMS_INPUT } from "./plan-selection";

function RoomCountField({
  id,
  label,
  hint,
  value,
  onChange,
  sliderMax = 2500,
}: {
  id: string;
  label: string;
  hint?: string;
  value: number;
  onChange: (value: number) => void;
  sliderMax?: number;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3">
        <Label htmlFor={id} className="text-sm font-medium text-gray-900">
          {label}
        </Label>
        <Input
          id={id}
          type="number"
          min={0}
          max={MAX_ROOMS_INPUT}
          value={value}
          onChange={(e) => onChange(e.target.value === "" ? 0 : Number(e.target.value))}
          className="w-24 text-right"
          data-testid={`input-${id}`}
        />
      </div>
      <Slider
        value={[Math.min(value, sliderMax)]}
        min={0}
        max={sliderMax}
        step={1}
        onValueChange={([v]) => onChange(v)}
        aria-label={label}
        data-testid={`slider-${id}`}
      />
      {hint && <p className="text-xs text-gray-500">{hint}</p>}
    </div>
  );
}

/** Discount chip keyed to the COMBINED room count, with "next tier" nudge. */
export function VolumeDiscountChip({ combinedRooms }: { combinedRooms: number }) {
  const percent = volumeDiscountPercent(combinedRooms);
  const nextTier = [...VOLUME_DISCOUNT_TIERS].reverse().find((t) => t.minRooms > combinedRooms);
  return (
    <div
      className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-medium ${
        percent > 0 ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-gray-50 text-gray-600 border border-gray-200"
      }`}
      data-testid="chip-volume-discount"
    >
      <TrendingDown size={14} className="shrink-0" />
      {percent > 0 ? (
        <span>
          {percent}% volume discount on your whole bill
          {nextTier ? ` — ${nextTier.minRooms - combinedRooms} more rooms unlocks ${nextTier.percent}%` : ""}
        </span>
      ) : nextTier ? (
        <span>
          Volume discounts start at {nextTier.minRooms} rooms ({nextTier.percent}% off your whole bill)
        </span>
      ) : null}
    </div>
  );
}

/**
 * Step 3: the portfolio — TWO room quantities, entered separately. Off-PadSplit
 * rooms carry the flat Member Portals surcharge on top of their other per-room
 * charges (those members have no PadSplit-native portal); the volume tier is
 * keyed to the combined count.
 */
export function RoomInputs({
  roomsOnPadsplit,
  roomsOffPadsplit,
  onChangeOn,
  onChangeOff,
}: {
  roomsOnPadsplit: number;
  roomsOffPadsplit: number;
  onChangeOn: (value: number) => void;
  onChangeOff: (value: number) => void;
}) {
  return (
    <div className="space-y-5">
      <RoomCountField
        id="rooms-on-padsplit"
        label="Rooms on PadSplit"
        value={roomsOnPadsplit}
        onChange={onChangeOn}
      />
      <RoomCountField
        id="rooms-off-padsplit"
        label="Rooms off PadSplit"
        hint={`+$${OFF_PADSPLIT_SURCHARGE_CENTS / 100}/room Member Portals surcharge — off-PadSplit members get PadPulse's own portal`}
        value={roomsOffPadsplit}
        onChange={onChangeOff}
      />
      <VolumeDiscountChip combinedRooms={roomsOnPadsplit + roomsOffPadsplit} />
    </div>
  );
}
